import { Link, Navigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowRight,
  ClipboardList,
  Gamepad2,
  Gift,
  Plus,
  Smartphone,
  Sparkles,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { useMe } from '../lib/session';
import { feedSnapshotQuery, leaderboardQuery, statsQuery } from '../lib/queries';
import { useAuthDialog, useStandalone } from '../components/AuthDialog';
import { useIsDesktop } from '../components/Layout';
import { BRANDS, BrandMark, type BrandKey } from '../components/BrandMarks';
import { CoinGlyph, CoinMascot, Swatch } from '../components/Coin';
import { type FeedItem } from '../components/LiveFeed';
import { Avatar } from '../components/Avatar';
import { coinsToUsd, feedName, fmtCoins, timeAgo } from '../lib/api';
import { T, fmtNumber, msg, t, tc } from '../i18n';

interface Stats {
  users: number;
  paidOutUsd: number;
  payouts: number;
}
interface Board {
  entries: { rank: number; username: string; coins: number; offers: number }[];
}

/** Same medal colours as the full leaderboard page. */
const MEDAL = ['bg-sun text-ink', 'bg-silver text-ink', 'bg-bronze text-ink'];

/** Whole US dollars the reader's way: $10, 10 $, 10 $US. */
const usd = (n: number) =>
  fmtNumber(n, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

/** Bold words in running text. */
const B = { b: (c: ReactNode) => <b className="font-bold text-ink">{c}</b> };

export function Landing() {
  const { data: me } = useMe();
  const { open } = useAuthDialog();
  const { data: stats } = useQuery(statsQuery<Stats>());
  const { data: feed } = useQuery({
    ...feedSnapshotQuery<{ items: FeedItem[] }>(),
    refetchInterval: 20_000,
  });
  const { data: board } = useQuery(leaderboardQuery<Board>('day'));
  // The installed app has no marketing page: its front door is the welcome screen at /signup.
  const desktop = useIsDesktop();
  const app = useStandalone() && !desktop;
  if (me) return <Navigate to="/earn" replace />;
  if (app) return <Navigate to="/signup" replace />;
  const signup = () => open('signup');

  return (
    <div className="overflow-x-clip pb-20 md:pb-32">
      {/* ---------- Hero ---------- */}
      <section className="relative">
        <div className="mx-auto grid max-w-[1200px] grid-cols-1 items-center gap-20 px-5 pb-16 pt-10 md:px-8 md:pb-20 md:pt-20 lg:grid-cols-[1.1fr_0.9fr] lg:gap-10 lg:pb-28">
          <div>
            <span className="sticker inline-flex -rotate-2 items-center gap-2 rounded-full bg-paper px-3.5 py-1.5 text-[13px] font-bold">
              <Swatch color="mint" />{' '}
              {
                // i18n: a small sticker above the home page headline: two selling points, keep it short
                t('Free to join · Cash out from $1')
              }
            </span>
            <h1 className="display mt-7 max-w-[11ch]">
              {
                // i18n: the home page headline, in the biggest type on the site, about 11 letters a line: keep it as short as the English. <hl> highlights a word with a marker stroke: put it around the word for “paid” (or the word that carries the promise)
                <T
                  msg="Get <hl>paid</hl> for the games you already play."
                  tags={{ hl: (c) => <span className="marker">{c}</span> }}
                />
              }
            </h1>
            <p className="mt-6 max-w-[34rem] text-pretty text-[17px] leading-relaxed text-graphite md:mt-7 md:text-[19px]">
              {t(
                'Play games, try apps and answer surveys from trusted partners. Every task pays coins, and 1,000 coins is $1. Swap them for gift cards whenever you like.',
              )}
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center md:mt-9">
              <button onClick={signup} className="btn-coin px-7 py-3.5 text-base">
                {
                  // i18n: the main sign-up button
                  t('Start earning — it’s free')
                }{' '}
                <ArrowRight size={18} aria-hidden="true" />
              </button>
              <button onClick={() => open('login')} className="btn-ghost px-6 py-3.5 text-base">
                {
                  // i18n: button that opens the log-in form
                  t('I have an account')
                }
              </button>
            </div>
            <p className="mt-6 text-[14px] tabular-nums text-stone md:mt-7">
              {stats && stats.paidOutUsd > 0 ? (
                // i18n: under the home page buttons: the total paid out so far and to how many members ({usd} is an amount in US dollars)
                <T
                  msg="<b>{usd}</b> paid out to {members, plural, one {<b>#</b> member} other {<b>#</b> members}} so far."
                  values={{ usd: usd(stats.paidOutUsd), members: stats.users }}
                  tags={B}
                />
              ) : (
                t('No card, no deposit. Sign up with an email in under a minute.')
              )}
            </p>
          </div>

          <HeroBoard onStart={signup} />
        </div>
      </section>

      {/* ---------- Facts + recent earnings ---------- */}
      {/* Cells carry their own 24px padding, so the grid is pulled out by 24px on wider screens to
          keep the first number on the same left edge as the hero copy. */}
      <section aria-label={t('GaintPlay in numbers')} className="border-y border-hairline bg-paper">
        <div className="mx-auto max-w-[1200px] md:px-8">
          <div className="grid gap-px bg-hairline md:-mx-6 md:grid-cols-3 lg:grid-cols-4">
            <Fact
              big={usd(1)}
              // i18n: label of a big figure ($1): the smallest amount you can cash out
              label={t('Minimum cash-out')}
            />
            <Fact
              big={fmtCoins(1000)}
              // i18n: label of a big figure (1,000): how many coins make one US dollar
              label={t('Coins per dollar')}
              coin
            />
            <Fact
              big={fmtNumber(6)}
              // i18n: label of a big figure (6): how many offer wall and survey companies GaintPlay works with
              label={t('Offer and survey partners')}
            />
            <div className="bg-paper px-5 py-5 md:col-span-3 md:px-6 md:py-7 lg:col-span-1">
              <div className="text-[13px] font-semibold text-stone">
                {
                  // i18n: heading of a short list of members' latest rewards
                  t('Recently earned')
                }
              </div>
              <ul className="mt-3 grid gap-2.5 md:grid-cols-3 md:gap-x-8 lg:grid-cols-1">
                {(feed?.items ?? []).slice(0, 3).map((it, i) => (
                  <li key={i} className="flex min-w-0 items-center gap-2 text-[14px]">
                    <Avatar name={it.username} size={24} />
                    <span className="min-w-0 truncate font-semibold">{feedName(it.username)}</span>
                    <span className="reward ml-auto shrink-0">
                      <CoinGlyph /> {fmtCoins(it.coins)}
                    </span>
                  </li>
                ))}
                {(!feed || feed.items.length === 0) && (
                  <li className="text-[14px] text-stone">
                    {
                      // i18n: shown when nobody has earned yet today, in place of the list of latest rewards
                      t('Be the first on the board today.')
                    }
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- How it works ---------- */}
      <Section
        id="how"
        eyebrow={t('How it works')}
        // i18n: section headline: two short punchy sentences, keep the rhythm
        title={t('Three steps. No catch.')}
      >
        <div className="grid grid-cols-1 gap-4 md:gap-5 lg:grid-cols-[1.25fr_1fr] lg:grid-rows-2">
          <Step
            n="1"
            // i18n: step 1 of earning: choose a task you'd enjoy doing anyway
            title={t('Pick a task you’d do anyway')}
            body={t(
              'Browse offer walls and survey routers from trusted partners. Each shows exactly what it pays before you start.',
            )}
            className="lg:row-span-2"
          >
            {/* A slice of the real Earn screen: category pills, then offers that stretch to fill
                the tall bento cell. */}
            <div className="mt-5 flex flex-wrap gap-1.5 sm:mt-6 sm:gap-2" aria-hidden="true">
              {[
                // i18n: filter button over the partner list: show every partner
                tc('partner filter', 'All'),
                t('Games'),
                t('Surveys'),
                t('Apps'),
              ].map((c, i) => (
                <span
                  key={c}
                  // One row on every phone: the fourth pill bows out below 360px.
                  className={`inline-flex items-center rounded-full border px-2.5 py-1.5 text-[13px] font-semibold sm:px-3 ${i === 0 ? 'border-ink bg-ink text-white' : 'border-hairline bg-paper text-graphite'} ${i === 3 ? 'max-[359px]:hidden' : ''}`}
                >
                  {c}
                </span>
              ))}
            </div>
            <div className="mt-3 grid flex-1 gap-3 sm:grid-cols-2 lg:auto-rows-fr">
              {SAMPLE.map((s, i) => (
                <MiniOffer key={s.title} {...s} className={i > 1 ? 'hidden sm:flex' : ''} />
              ))}
            </div>
          </Step>
          <Step
            n="2"
            // i18n: step 2 of earning: coins arrive in your balance without you doing anything
            title={t('Coins land automatically')}
            body={t(
              'When the partner confirms you finished, coins hit your balance. Bigger rewards wait 7 to 30 days so partners can confirm them.',
            )}
          >
            <div className="mt-auto pt-5">
              <div className="flex items-center gap-3 rounded-[14px] border border-hairline bg-canvas px-4 py-3">
                <CoinGlyph className="h-6 w-6" />
                <div className="text-[22px] font-extrabold tabular-nums tracking-tight">
                  {fmtCoins(2650)}
                </div>
                <span className="reward ml-auto">
                  <CoinGlyph /> {fmtNumber(650, { signDisplay: 'always' })}
                </span>
              </div>
            </div>
          </Step>
          <Step
            n="3"
            // i18n: step 3 of earning
            title={t('Cash out from $1')}
            body={t(
              'Swap coins for gift cards from hundreds of brands. Every request is reviewed, then delivered to your email.',
            )}
          >
            <div className="mt-auto pt-5">
              <p className="sr-only">
                {
                  // i18n: read by screen readers in place of three small pictured gift cards
                  t('Gift cards from brands like Crunchyroll, Steam and Spotify')
                }
              </p>
              <div className="flex gap-2" aria-hidden="true">
                {(
                  [
                    ['crunchyroll', 5, -3],
                    ['steam', 10, 0],
                    ['spotify', 25, 3],
                  ] as const
                ).map(([brand, value, tilt]) => (
                  <MiniGiftCard key={brand} brand={brand} value={value} tilt={tilt} />
                ))}
              </div>
            </div>
          </Step>
        </div>
        <MoreLink to="/how-it-works">{t('See every step, with timings')}</MoreLink>
      </Section>

      {/* ---------- Ways to earn ---------- */}
      <Section
        id="ways"
        eyebrow={t('Ways to earn')}
        title={t('Something for a spare five minutes, or a whole weekend.')}
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-5 lg:grid-cols-4">
          {WAYS.map((w) => (
            <div key={w.title} className="tile flex flex-col p-5 sm:p-6">
              <div className="flex items-center gap-4 sm:block">
                <div className="grid h-12 w-12 shrink-0 place-items-center rounded-[12px] bg-canvas">
                  <w.icon size={22} strokeWidth={1.75} aria-hidden="true" />
                </div>
                <h3 className="text-[20px] font-bold tracking-[-0.01em] sm:mt-6">{t(w.title)}</h3>
              </div>
              <p className="mt-3 flex-1 text-pretty text-[15px] leading-relaxed text-graphite sm:mt-2">
                {t(w.body)}
              </p>
              <div className="mt-5 border-t border-hairline pt-4">
                <div className="text-[13px] font-semibold text-stone">
                  {
                    // i18n: above a range of coins a kind of task usually pays
                    t('Typical reward')
                  }
                </div>
                <div className="mt-2">
                  <span className="reward">
                    <CoinGlyph />{' '}
                    {
                      // i18n: a range of coins, lowest to highest (“2,000 – 50,000”)
                      t('{min} – {max}', { min: w.range[0], max: w.range[1] })
                    }
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
        <p className="mt-5 text-[13px] text-stone">
          {t('Rewards are set by each partner and vary by country and device.')}
        </p>
        <MoreLink to="/ways-to-earn" className="mt-2">
          {t('All the ways to earn')}
        </MoreLink>
      </Section>

      {/* ---------- Cash out ---------- */}
      <Section
        id="cashout"
        // i18n: the name of the Cash out page (and of the app's Cash out screen), above a section about it
        eyebrow={t('Cash out')}
        title={t('Real gift cards, not points that expire.')}
      >
        <div className="tile relative grid grid-cols-1 items-center gap-8 overflow-hidden p-5 sm:gap-10 sm:p-8 lg:grid-cols-2 lg:p-12">
          <div>
            <ul className="space-y-4 text-[16px] leading-relaxed text-graphite md:text-[17px]">
              {[
                // i18n: a selling point of cashing out; <b> is the point in short (bold), then a short sentence
                msg('<b>Start at $1.</b> No waiting until you hit $20.'),
                // i18n: a selling point of cashing out; <b> is the point in short (bold), then a short sentence
                msg('<b>Hundreds of brands.</b> Shopping, games, food and streaming.'),
                // i18n: a selling point of cashing out; <b> is the point in short (bold), then a short sentence
                msg('<b>Delivered by email.</b> Most requests are reviewed in minutes.'),
                // i18n: a selling point of cashing out; <b> is the point in short (bold), then a short sentence
                msg('<b>Your coins never expire.</b> Cash out today or save up.'),
              ].map((point) => (
                <li key={point} className="flex items-start gap-3">
                  {/* One line tall, so the marker centres on the first line of text. */}
                  <span className="flex h-[1.625em] shrink-0 items-center">
                    <Swatch color="coin" />
                  </span>
                  <span>
                    <T msg={point} tags={B} />
                  </span>
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-6 md:mt-9">
              <button onClick={signup} className="btn-primary w-full sm:w-auto">
                {t('Create a free account')}
              </button>
              <MoreLink to="/gift-cards" className="mt-0 self-center sm:self-auto">
                {t('How cash-outs work')}
              </MoreLink>
            </div>
          </div>
          <div>
            <p className="sr-only">
              {
                // i18n: read by screen readers in place of three pictured gift cards
                t('Example gift cards: Steam, Crunchyroll and Spotify.')
              }
            </p>
            <div
              className="relative mx-auto h-[220px] w-full max-w-[440px] sm:h-[250px]"
              aria-hidden="true"
            >
              <GiftCard className="left-0 top-5 -rotate-6" brand="steam" value={10} />
              <GiftCard className="right-0 top-0 rotate-6" brand="crunchyroll" value={5} />
              <GiftCard
                className="bottom-0 left-1/2 -translate-x-1/2 rotate-1"
                brand="spotify"
                value={25}
              />
            </div>
          </div>
        </div>
        <p className="mt-5 text-pretty text-[13px] text-stone">
          {
            // i18n: legal notice under pictured gift cards
            t(
              'Brand names and logos belong to their owners, who don’t sponsor GaintPlay. Card availability varies by country.',
            )
          }
        </p>
      </Section>

      {/* ---------- Leaderboard preview ---------- */}
      {board && board.entries.length > 0 && (
        <Section eyebrow={t('Leaderboard')} title={t('Today’s top earners.')}>
          <ol className="tile divide-y divide-hairline">
            {board.entries.slice(0, 5).map((e) => (
              <li
                key={e.rank}
                className="flex items-center gap-3 px-4 py-3.5 sm:gap-4 sm:px-6 sm:py-4"
              >
                <span
                  className={`grid h-8 w-8 shrink-0 place-items-center rounded-full text-[14px] font-extrabold tabular-nums ${MEDAL[e.rank - 1] ?? 'bg-canvas text-graphite'}`}
                >
                  {e.rank}
                </span>
                {/* Below 360px the name needs the room more than the face does. */}
                <span className="shrink-0 max-[359px]:hidden">
                  <Avatar name={e.username} size={36} />
                </span>
                <span className="min-w-0 truncate font-semibold">{e.username}</span>
                <span className="hidden shrink-0 text-[13px] text-stone sm:inline">
                  {
                    // i18n: how many offers a member on the leaderboard completed
                    t('{n, plural, one {# offer} other {# offers}}', { n: e.offers })
                  }
                </span>
                <span className="reward ml-auto shrink-0">
                  <CoinGlyph /> {fmtCoins(e.coins)}
                </span>
              </li>
            ))}
          </ol>
          <Link
            to="/leaderboard"
            className="group mt-5 inline-flex min-h-10 items-center gap-1.5 rounded-[10px] text-[15px] font-semibold text-ink"
          >
            <span className="underline decoration-hairline decoration-2 underline-offset-4 transition group-hover:decoration-ink">
              {t('See the full leaderboard')}
            </span>
            <ArrowRight
              size={16}
              aria-hidden="true"
              className="transition-transform group-hover:translate-x-0.5"
            />
          </Link>
        </Section>
      )}

      {/* ---------- FAQ ---------- */}
      <Section
        id="faq"
        // i18n: above the frequently asked questions on the home page
        eyebrow={t('Questions')}
        title={t('Good questions, straight answers.')}
      >
        <div className="grid gap-x-10 border-t border-hairline md:grid-cols-2 md:border-t-0">
          {FAQ.map(([q, a], i) => (
            <details
              key={q}
              className={`group border-b border-hairline ${i < 2 ? 'md:border-t' : ''}`}
            >
              <summary className="flex min-h-14 cursor-pointer list-none items-center justify-between gap-4 rounded-[6px] py-4 text-[17px] font-semibold leading-snug [&::-webkit-details-marker]:hidden">
                {t(q)}
                <Plus
                  size={18}
                  aria-hidden="true"
                  className="shrink-0 text-graphite transition group-open:rotate-45"
                />
              </summary>
              <p className="max-w-[60ch] text-pretty pb-5 pr-8 text-[16px] leading-relaxed text-graphite">
                {t(a)}
              </p>
            </details>
          ))}
        </div>
        <MoreLink to="/faq">{t('Read the full FAQ')}</MoreLink>
      </Section>
    </div>
  );
}

/* ---------- Pieces ---------- */

/** A quiet text link to the full guide page behind a landing section (pre-rendered, SEO). */
function MoreLink({
  to,
  children,
  className = 'mt-5',
}: {
  to: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <Link
      to={to}
      className={`group inline-flex min-h-10 items-center gap-1.5 rounded-[10px] text-[15px] font-semibold text-ink ${className}`}
    >
      <span className="underline decoration-hairline decoration-2 underline-offset-4 transition group-hover:decoration-ink">
        {children}
      </span>
      <ArrowRight
        size={16}
        aria-hidden="true"
        className="transition-transform group-hover:translate-x-0.5"
      />
    </Link>
  );
}

type Kind = 'game' | 'survey' | 'app' | 'offer';

/** Made-up example offers, as the Earn screen shows real ones. */
const SAMPLE: { icon: typeof Gamepad2; title: string; kind: Kind; coins: number }[] = [
  {
    icon: Gamepad2,
    // i18n: a made-up example offer's title, on a small card (two lines at most)
    title: msg('Reach level 25 in a strategy game'),
    kind: 'game',
    coins: 18500,
  },
  {
    icon: ClipboardList,
    // i18n: a made-up example offer's title, on a small card (two lines at most)
    title: msg('10-minute opinion survey'),
    kind: 'survey',
    coins: 650,
  },
  {
    icon: Smartphone,
    // i18n: a made-up example offer's title, on a small card (two lines at most)
    title: msg('Install and open a budgeting app'),
    kind: 'app',
    coins: 2400,
  },
  {
    icon: Sparkles,
    // i18n: a made-up example offer's title, on a small card (two lines at most)
    title: msg('Start a free streaming trial'),
    kind: 'offer',
    coins: 4200,
  },
];

/** The kind of an example offer, under its title. */
function kindLabel(kind: Kind) {
  // i18n: the kind of an example offer, small, under its title
  return t('{kind, select, game {Game} survey {Survey} app {App} other {Offer}}', { kind });
}

/** The kinds of task, each with its typical reward in coins (lowest, highest). */
const WAYS = [
  {
    icon: Gamepad2,
    // i18n: a kind of task
    title: msg('Games'),
    body: msg(
      'Install a game and hit milestones like reaching a level. The biggest rewards live here.',
    ),
    range: [2000, 50000],
  },
  {
    icon: ClipboardList,
    // i18n: a kind of task
    title: msg('Surveys'),
    body: msg('Share opinions in 5 to 20 minutes. Matched to your profile, new ones every hour.'),
    range: [200, 2000],
  },
  {
    icon: Smartphone,
    // i18n: a kind of task
    title: msg('Apps'),
    body: msg('Try new apps and keep the ones you like. Usually done in one sitting.'),
    range: [500, 5000],
  },
  {
    icon: Gift,
    // i18n: a kind of task: free trials and sign-ups from brands
    title: msg('Offers'),
    body: msg('Free trials and sign-ups from brands. Read the terms, finish the steps, get paid.'),
    range: [300, 10000],
  },
] as const;

/** A few questions from the FAQ, question then answer. */
const FAQ: [string, string][] = [
  [
    msg('Is GaintPlay really free?'),
    msg(
      'Yes. You never pay to join or to cash out. Partners pay us for the tasks you complete and we share that with you in coins.',
    ),
  ],
  [
    msg('How much is a coin worth?'),
    msg(
      '1,000 coins = $1.00. Every task shows its reward in coins before you start, so you always know what you will get.',
    ),
  ],
  [
    msg('How do I get paid?'),
    // i18n: Cash out: the app screen where coins are exchanged (use its name in the menu)
    msg(
      'Pick a gift card in Cash out, choose an amount from $1, and confirm. Each request is reviewed, then the card is emailed to you.',
    ),
  ],
  [
    msg('Why are some of my coins pending?'),
    msg(
      'Larger rewards wait 7 to 30 days so the partner can confirm the task was done properly. They release on their own.',
    ),
  ],
  [
    msg('A task didn’t pay. What now?'),
    // i18n: Help: the app's support screen (use its name in the menu)
    msg(
      'Partners credit coins after they confirm. If it has been more than a day, contact the offer wall’s support from inside the wall, or ask us in Help.',
    ),
  ],
  [
    // i18n: a question from someone who used GaintPlay's earlier site, before this one
    msg('I had an account on the old GaintPlay.'),
    // i18n: Wallet: the app screen with the balance (use its name in the menu). Write the date your language's way
    msg(
      'Sign up with any email, then connect your old account from your Wallet by 30 November 2026. Up to 2,000 coins arrive straight away; larger balances are approved by our team first.',
    ),
  ],
];

function Section({
  id,
  eyebrow,
  title,
  children,
}: {
  id?: string;
  eyebrow: string;
  title: string;
  children: ReactNode;
}) {
  // Top padding only, so the gap between two sections is one step of the scale, not two.
  // The anchor sits on the content (not the padded section) and clears the sticky header.
  return (
    <section className="pt-16 md:pt-32">
      <div id={id} className="mx-auto max-w-[1200px] scroll-mt-28 px-5 md:px-8">
        <div className="eyebrow flex items-center gap-2">
          <Swatch color="coin" /> {eyebrow}
        </div>
        <h2 className="heading-lg mt-3 max-w-[22ch]">{title}</h2>
        <div className="mt-8 md:mt-12">{children}</div>
      </div>
    </section>
  );
}

/** Stat block: label above, big tabular number. On phones it reads as a ledger row. */
function Fact({ big, label, coin = false }: { big: string; label: string; coin?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-4 bg-paper px-5 py-4 md:flex-col md:items-start md:px-6 md:py-7">
      <div className="text-[14px] text-stone md:text-[13px] md:font-semibold">{label}</div>
      <div className="flex items-center gap-2 text-[28px] font-extrabold leading-none tracking-[-0.03em] tabular-nums md:mt-3 md:text-[40px]">
        {coin && <CoinGlyph className="h-6 w-6 md:h-8 md:w-8" />}
        {big}
      </div>
    </div>
  );
}

function Step({
  n,
  title,
  body,
  className = '',
  children,
}: {
  n: string;
  title: string;
  body: string;
  className?: string;
  children?: ReactNode;
}) {
  return (
    <div className={`tile flex flex-col p-5 sm:p-6 md:p-8 ${className}`}>
      <span className="sticker inline-grid h-9 w-9 place-items-center self-start rounded-full bg-paper text-[15px] font-extrabold">
        <span className="sr-only">
          {
            // i18n: the number of a step (“Step 1”), above its name
            t('Step {n}', { n })
          }
        </span>
        <span aria-hidden="true">{n}</span>
      </span>
      <h3 className="mt-5 text-balance text-[22px] font-bold leading-tight tracking-[-0.02em] md:text-[24px]">
        {title}
      </h3>
      <p className="mt-2 max-w-md text-pretty text-[15px] leading-relaxed text-graphite">{body}</p>
      {children}
    </div>
  );
}

function MiniOffer({
  icon: Icon,
  title,
  kind,
  coins,
  className = '',
}: {
  icon: typeof Gamepad2;
  title: string;
  kind: Kind;
  coins: number;
  className?: string;
}) {
  return (
    <div className={`card flex flex-col justify-between gap-4 p-4 ${className}`}>
      <div className="flex items-center justify-between">
        <div className="grid h-10 w-10 place-items-center rounded-[10px] bg-canvas">
          <Icon size={18} strokeWidth={1.75} aria-hidden="true" />
        </div>
        <span className="reward">
          <CoinGlyph /> {fmtCoins(coins)}
        </span>
      </div>
      <div>
        <div className="line-clamp-2 text-[15px] font-semibold leading-snug">{t(title)}</div>
        <div className="mt-1 text-[12px] font-semibold text-stone">{kindLabel(kind)}</div>
      </div>
    </div>
  );
}

/** Hero visual: a believable slice of the Earn screen, not an abstract illustration. */
function HeroBoard({ onStart }: { onStart: () => void }) {
  return (
    <div className="relative w-full max-w-[460px] lg:ml-auto">
      {/* The big coin peeks over the top-right corner, clear of the "= $12.48" pill. */}
      <CoinMascot
        size={116}
        rotate={14}
        float
        className="absolute -right-2 -top-[68px] z-10 h-[84px] w-[84px] sm:-right-8 sm:-top-[88px] sm:h-[116px] sm:w-[116px] xl:-right-10"
      />
      <CoinMascot
        size={60}
        rotate={-18}
        className="absolute -left-5 top-[58%] z-10 hidden sm:block lg:-left-9"
      />
      <CoinMascot size={38} rotate={8} className="absolute -bottom-5 right-16 z-10" />
      <div className="tile relative p-4 sm:p-5">
        <div className="flex items-center justify-between gap-3 px-1">
          <div>
            <div className="text-[13px] font-semibold text-stone">
              {
                // i18n: above an example coin balance on a picture of the app
                t('Your balance')
              }
            </div>
            <div className="mt-1 flex items-center gap-2 text-[30px] font-extrabold leading-none tracking-[-0.03em] tabular-nums">
              <CoinGlyph className="h-7 w-7" /> {fmtCoins(12480)}
            </div>
          </div>
          <span className="whitespace-nowrap rounded-full bg-ink px-3 py-1.5 text-[13px] font-bold tabular-nums text-white">
            = {coinsToUsd(12480)}
          </span>
        </div>
        <div className="mt-5 space-y-2.5">
          {SAMPLE.slice(0, 3).map((s, i) => (
            <button
              key={s.title}
              onClick={onStart}
              className="flex w-full items-center gap-3 rounded-[14px] border border-hairline bg-paper p-3 text-left transition hover:border-ink"
            >
              <div className="grid h-11 w-11 shrink-0 place-items-center rounded-[10px] bg-canvas">
                <s.icon size={20} strokeWidth={1.75} aria-hidden="true" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="line-clamp-2 text-[15px] font-semibold leading-snug">
                  {t(s.title)}
                </div>
                <div className="mt-0.5 flex items-center gap-2 text-[12px] font-semibold text-stone">
                  {kindLabel(s.kind)}
                  {i === 0 && (
                    <span className="rounded-full bg-sun px-1.5 py-px text-[10px] font-extrabold tracking-wide text-ink">
                      {
                        // i18n: a tiny badge on a popular offer, written in capitals: one short word
                        t('HOT')
                      }
                    </span>
                  )}
                  {/* Narrow phones: the reward moves under the title so the title gets the width. */}
                  <span className="reward ml-auto min-[400px]:hidden">
                    <CoinGlyph /> {fmtCoins(s.coins)}
                  </span>
                </div>
              </div>
              <span className="reward shrink-0 max-[399px]:hidden">
                <CoinGlyph /> {fmtCoins(s.coins)}
              </span>
            </button>
          ))}
        </div>
        <div className="mt-4 flex items-center justify-between px-1 text-[12px] text-stone">
          <span>
            {
              // i18n: caption under a picture of the app's offers: they're examples, not real offers
              t('Example offers')
            }
          </span>
          <span>
            {
              // i18n: {ago} is how long ago the list was updated (“2m ago”), then how many offers are new
              t('{ago} · {n, plural, one {# new} other {# new}}', {
                ago: timeAgo(Date.now() - 120_000),
                n: 3,
              })
            }
          </span>
        </div>
      </div>
    </div>
  );
}

/** A real-looking gift card in the brand's own colours: mark and name on top, value below. */
function GiftCard({
  className,
  brand,
  value,
}: {
  className: string;
  brand: BrandKey;
  value: number;
}) {
  const b = BRANDS[brand];
  return (
    <div
      className={`sticker absolute flex h-24 w-[9.5rem] flex-col justify-between rounded-[16px] p-3.5 sm:h-32 sm:w-52 sm:p-4 ${className}`}
      style={{ background: b.bg, color: b.fg }}
    >
      <div className="flex min-w-0 items-center gap-2">
        <BrandMark brand={brand} size={22} />
        <span className="truncate text-[14px] font-bold tracking-[-0.01em] sm:text-[15px]">
          {b.name}
        </span>
      </div>
      <div className="flex items-end justify-between gap-2">
        <span className="text-[28px] font-extrabold leading-none tracking-[-0.03em] tabular-nums sm:text-[34px]">
          {usd(value)}
        </span>
        <span className="text-[11px] font-semibold opacity-75 sm:text-[12px]">
          {
            // i18n: on a picture of a gift card, beside its value: keep it short
            t('Gift card')
          }
        </span>
      </div>
    </div>
  );
}

/** The small tilted cards in "Cash out from $1". The name drops when a card gets too narrow for
 *  it (three in a row on a 320px phone); the mark and the value always stay. */
function MiniGiftCard({ brand, value, tilt }: { brand: BrandKey; value: number; tilt: number }) {
  const b = BRANDS[brand];
  return (
    <div
      className="@container flex h-20 min-w-0 flex-1 flex-col justify-between rounded-[10px] border-[1.5px] border-ink p-2.5 shadow-[2px_2px_0_var(--color-ink)]"
      style={{ background: b.bg, color: b.fg, transform: `rotate(${tilt}deg)` }}
    >
      <BrandMark brand={brand} size={20} />
      <div className="min-w-0">
        <div className="truncate text-[12px] font-bold leading-tight @max-[74px]:hidden">
          {b.name}
        </div>
        <div className="mt-0.5 text-[20px] font-extrabold leading-none tracking-[-0.02em] tabular-nums">
          {usd(value)}
        </div>
      </div>
    </div>
  );
}
